import React, { useState, useRef, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useUser } from "@clerk/clerk-react";
import { useQuery, useMutation, useAction } from "convex/react";
import { api } from "../../../../../../convex/_generated/api";
import { Id } from "../../../../../../convex/_generated/dataModel";
import { DashboardLayout } from "@/components/dashboard/dashboard-layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  CheckCircle2, 
  Circle, 
  Loader2, 
  Mail, 
  FileText, 
  PenTool, 
  ArrowRight,
  Clock,
  Send,
  Edit
} from "lucide-react";
import SignatureCanvas from "react-signature-canvas";
import { toast } from "sonner";
import { useTypstCompiler } from "@/hooks/useTypstCompiler";

type WorkflowStep = "prepare" | "customize_signature_page" | "provider_sign" | "client_request" | "waiting" | "completed";

const DEFAULT_SIGNATURE_PARAGRAPH = "IN WITNESS WHEREOF, the parties hereto have executed this Janitorial Service Agreement as of the date first written below. Each individual signing below represents and warrants that they are duly authorized to execute and deliver this Agreement on behalf of their respective party.";

export default function DocumentSigningWorkflowPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const documentId = id as Id<"documents">;
  
  const document = useQuery(api.documents.getDocument, { documentId });
  
  const { compile, isReady: isCompilerReady } = useTypstCompiler();
  const finalizeDocumentAction = useAction(api.documentFinalizer.finalizeDocument);
  const initiateSigningMutation = useMutation(api.documents.initiateSigningWorkflow);
  const submitSignatureMutation = useMutation(api.documents.submitSignature);
  const updateSignatureParagraphMutation = useMutation(api.documents.updateSignatureParagraph);

  const { user } = useUser();
  const clientBusiness = useQuery(api.businesses.getBusinessById, document?.relatedClient ? { id: document.relatedClient as Id<"businesses"> } : "skip");

  const [currentStep, setCurrentStep] = useState<WorkflowStep>("prepare");
  const [isLoading, setIsLoading] = useState(false);
  const [clientName, setClientName] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  
  const [hasCustomizedSignature, setHasCustomizedSignature] = useState(false);
  const [customParagraphText, setCustomParagraphText] = useState("");
  const [isSettingParagraph, setIsSettingParagraph] = useState(false);

  // Signature State
  const [activeTab, setActiveTab] = useState("draw");
  const [typedSignature, setTypedSignature] = useState("");
  const [selectedFont, setSelectedFont] = useState("'Caveat', cursive");
  const [signerTitle, setSignerTitle] = useState("");
  const sigCanvas = useRef<SignatureCanvas>(null);
  const hiddenCanvasRef = useRef<HTMLCanvasElement>(null);

  // Sync current step with document status
  useEffect(() => {
    if (!document) return;

    if (document.status === "signed") {
      setCurrentStep("completed");
    } else if (document.status === "signing_pending") {
      const providerSigner = document.signers?.find(s => s.role === "provider");
      const clientSigner = document.signers?.find(s => s.role === "client");
      
      if (!providerSigner || providerSigner.status === "pending") {
        setCurrentStep("provider_sign");
      } else if (clientSigner && clientSigner.status === "pending") {
        setCurrentStep("waiting");
      }
    } else if (document.status === "finalized_ready_for_signature") {
      const providerSigner = document.signers?.find(s => s.role === "provider");
      if (providerSigner && providerSigner.status === "signed") {
        setCurrentStep("client_request");
      } else if (!hasCustomizedSignature) {
        setCurrentStep("customize_signature_page");
        setCustomParagraphText(prev => prev || document.customSignatureParagraph || DEFAULT_SIGNATURE_PARAGRAPH);
      } else {
        setCurrentStep("provider_sign");
      }
    } else {
      setCurrentStep("prepare");
    }

    if (document.signers) {
      const existingClient = document.signers.find(s => s.role === "client");
      if (existingClient) {
        if (!clientName && existingClient.name) setClientName(existingClient.name);
        if (!clientEmail && existingClient.email) setClientEmail(existingClient.email);
      }
    }

    if (clientBusiness) {
      if (!clientName && clientBusiness.contactName) setClientName(clientBusiness.contactName);
      else if (!clientName && clientBusiness.name) setClientName(clientBusiness.name);
      
      if (!clientEmail && clientBusiness.contactEmail) setClientEmail(clientBusiness.contactEmail);
    }
  }, [document, clientBusiness]);

  const handleSaveSignatureParagraph = async () => {
    setIsSettingParagraph(true);
    try {
      const textToSave = customParagraphText === DEFAULT_SIGNATURE_PARAGRAPH ? undefined : customParagraphText;
      if (textToSave !== document?.customSignatureParagraph) {
        await updateSignatureParagraphMutation({
          documentId,
          customSignatureParagraph: textToSave,
        });
      }
      setHasCustomizedSignature(true);
      setCurrentStep("provider_sign");
      toast.success("Signature page customized.");
    } catch (error) {
      console.error("Error saving signature paragraph:", error);
      alert("Failed to save signature paragraph.");
    } finally {
      setIsSettingParagraph(false);
    }
  };

  const handlePrepareDocument = async () => {
    setIsLoading(true);
    try {
      if (document?.typstContent && !document.compiledPdfStorageId) {
        if (!isCompilerReady) throw new Error("PDF compiler is not ready yet. Please wait a moment.");
        
        const blob = await compile(document.typstContent);
        if (!blob) throw new Error("Failed to compile document to PDF");
        
        const base64Data = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            const dataUrl = reader.result as string;
            resolve(dataUrl.split(',')[1]);
          };
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
        
        await finalizeDocumentAction({ documentId, pdfBlob: base64Data });
      }
      // Step will update automatically via useEffect once status changes
    } catch (error) {
      console.error("Error preparing document:", error);
      alert(error instanceof Error ? error.message : "Failed to prepare document.");
    } finally {
      setIsLoading(false);
    }
  };

  const clearSignature = () => {
    if (activeTab === "draw" && sigCanvas.current) {
      sigCanvas.current.clear();
    } else {
      setTypedSignature("");
    }
  };

  const handleProviderSign = async () => {
    setIsLoading(true);
    try {
      let dataUrl = "";
      if (activeTab === "draw") {
        if (sigCanvas.current?.isEmpty()) {
          alert("Please provide a signature first.");
          setIsLoading(false);
          return;
        }
        dataUrl = sigCanvas.current?.getTrimmedCanvas().toDataURL("image/png") || "";
      } else if (activeTab === "type") {
        if (!typedSignature.trim()) {
          alert("Please type your name to sign.");
          setIsLoading(false);
          return;
        }
        const canvas = hiddenCanvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.font = `48px ${selectedFont}`;
        ctx.fillStyle = "black";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(typedSignature, canvas.width / 2, canvas.height / 2);

        dataUrl = canvas.toDataURL("image/png");
      }

      await submitSignatureMutation({
        documentId,
        role: "provider",
        signatureDataUrl: dataUrl,
        title: signerTitle,
      });

      setCurrentStep("client_request");
    } catch (error) {
      console.error("Error submitting signature:", error);
      alert("Failed to submit signature.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendToClient = async () => {
    if (!clientEmail) {
      alert("Please enter a valid client email address.");
      return;
    }
    setIsLoading(true);
    try {
      await initiateSigningMutation({
        documentId,
        providerName: user?.fullName || "Provider",
        providerEmail: user?.primaryEmailAddress?.emailAddress || "",
        clientEmail,
        clientName: clientName || "Client",
        appUrl: window.location.origin,
      });
      toast.success("Signature request sent successfully.");
      setCurrentStep("waiting");
    } catch (error) {
      console.error("Error sending request:", error);
      alert("Failed to send signature request.");
    } finally {
      setIsLoading(false);
    }
  };

  if (!document) {
    return (
      <DashboardLayout activeTab="Documents">
        <div className="flex h-64 items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      </DashboardLayout>
    );
  }

  const steps = [
    { id: "prepare", title: "Prepare Document", description: "Compile and finalize the PDF for signing.", icon: FileText },
    { id: "customize_signature_page", title: "Customize Signature", description: "Customize the closing paragraph before signing.", icon: Edit },
    { id: "provider_sign", title: "Provider Signature", description: "Apply your signature to the agreement.", icon: PenTool },
    { id: "client_request", title: "Client Request", description: "Send an email request to the client to sign.", icon: Send },
    { id: "waiting", title: "Wait for Client", description: "Waiting for the client to complete the signature process.", icon: Clock },
    { id: "completed", title: "Completed", description: "The agreement is fully signed and finalized.", icon: CheckCircle2 },
  ];

  const currentStepIndex = steps.findIndex(s => s.id === currentStep);

  return (
    <DashboardLayout activeTab="Documents">
      <div className="mx-auto max-w-4xl py-8">
        <div className="mb-8">
          <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4">
            ← Back to Document
          </Button>
          <h1 className="text-3xl font-bold text-gray-900">Document Signing Workflow</h1>
          <p className="text-gray-500 mt-2">
            Complete the steps below to finalize and execute the service agreement.
          </p>
        </div>

        <div className="flex flex-col gap-6 lg:flex-row">
          {/* Timeline Sidebar */}
          <div className="w-full lg:w-1/3">
            <Card className="p-6">
              <h2 className="mb-6 text-lg font-bold">Workflow Progress</h2>
              <div className="relative space-y-8 before:absolute before:inset-0 before:ml-5 before:-translate-x-px before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-gray-200 before:to-transparent">
                {steps.map((step, index) => {
                  const isActive = step.id === currentStep;
                  const isPast = index < currentStepIndex;
                  const isFuture = index > currentStepIndex;

                  return (
                    <div key={step.id} className={`relative flex items-start gap-4 ${isFuture ? 'opacity-50' : ''}`}>
                      <div className={`relative z-10 flex h-10 w-10 shrink-0 items-center justify-center rounded-full border-2 ${
                        isPast || (isActive && step.id === "completed") ? "border-green-600 bg-green-50 text-green-600" :
                        isActive ? "border-blue-600 bg-blue-50 text-blue-600" :
                        "border-gray-300 bg-white text-gray-400"
                      }`}>
                        {isPast ? <CheckCircle2 className="h-5 w-5" /> : <step.icon className="h-5 w-5" />}
                      </div>
                      <div className="pt-1">
                        <h3 className={`font-semibold ${isActive && step.id === "completed" ? "text-green-900" : isActive ? "text-blue-900" : "text-gray-900"}`}>
                          {step.title}
                        </h3>
                        <p className="text-sm text-gray-500 mt-1">{step.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>
          </div>

          {/* Action Area */}
          <div className="w-full lg:w-2/3">
            <Card className="p-8 min-h-[400px]">
              {currentStep === "customize_signature_page" && (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">Customize Signature Page</h3>
                    <p className="text-gray-500 mt-2">
                      Review and customize the "IN WITNESS WHEREOF" paragraph that will appear on the final signature page.
                    </p>
                  </div>
                  <div className="mt-4">
                    <Textarea 
                      value={customParagraphText}
                      onChange={(e) => setCustomParagraphText(e.target.value)}
                      className="min-h-[150px] text-base"
                    />
                  </div>
                  {customParagraphText !== DEFAULT_SIGNATURE_PARAGRAPH && (
                    <div className="flex justify-end">
                      <Button variant="ghost" onClick={() => setCustomParagraphText(DEFAULT_SIGNATURE_PARAGRAPH)} size="sm">
                        Reset to Default
                      </Button>
                    </div>
                  )}
                  <div className="pt-4 border-t border-gray-100 flex justify-end">
                    <Button 
                      onClick={handleSaveSignatureParagraph} 
                      disabled={isSettingParagraph} 
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      {isSettingParagraph ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                      Save & Continue
                    </Button>
                  </div>
                </div>
              )}

              {currentStep === "prepare" && (
                <div className="flex flex-col items-center justify-center text-center h-full space-y-6 pt-10">
                  <div className="h-20 w-20 rounded-full bg-blue-50 flex items-center justify-center">
                    <FileText className="h-10 w-10 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">Prepare Document</h3>
                    <p className="text-gray-500 mt-2 max-w-md mx-auto">
                      Generate the final PDF version of the agreement. Once generated, the content will be locked and ready for signatures.
                    </p>
                  </div>
                  <Button 
                    size="lg" 
                    className="w-full max-w-sm mt-4 bg-blue-600 hover:bg-blue-700 text-lg"
                    onClick={handlePrepareDocument}
                    disabled={isLoading}
                  >
                    {isLoading ? <><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Generating PDF...</> : "Generate Final PDF"}
                  </Button>
                </div>
              )}

              {currentStep === "provider_sign" && (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">Provider Signature</h3>
                    <p className="text-gray-500 mt-2">
                      Please sign the document below. This signature will be placed on the finalized agreement.
                    </p>
                  </div>

                  <div className="mt-4 mb-2 max-w-md">
                    <label className="text-sm font-medium text-gray-700">Your Title (Optional)</label>
                    <Input
                      type="text"
                      placeholder="e.g. CEO, Manager"
                      value={signerTitle}
                      onChange={(e) => setSignerTitle(e.target.value)}
                      className="mt-1"
                    />
                  </div>

                  <Tabs defaultValue="draw" onValueChange={setActiveTab} className="w-full">
                    <TabsList className="grid w-full grid-cols-2 max-w-md">
                      <TabsTrigger value="draw">Draw Signature</TabsTrigger>
                      <TabsTrigger value="type">Type Signature</TabsTrigger>
                    </TabsList>

                    <TabsContent value="draw" className="mt-4">
                      <div className="rounded-md border bg-gray-50">
                        <SignatureCanvas
                          ref={sigCanvas}
                          penColor="black"
                          canvasProps={{ className: "w-full h-48 rounded-md" }}
                        />
                      </div>
                    </TabsContent>

                    <TabsContent value="type" className="mt-4 space-y-4">
                      <div className="flex gap-2">
                        <Input
                          placeholder="Type your full name"
                          value={typedSignature}
                          onChange={(e) => setTypedSignature(e.target.value)}
                          className="flex-1 text-lg"
                        />
                        <Select value={selectedFont} onValueChange={setSelectedFont}>
                          <SelectTrigger className="w-[180px]">
                            <SelectValue placeholder="Select Font" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="'Caveat', cursive">
                              <span style={{ fontFamily: "'Caveat', cursive", fontSize: "1.4rem" }}>Signature</span>
                            </SelectItem>
                            <SelectItem value="'Dancing Script', cursive">
                              <span style={{ fontFamily: "'Dancing Script', cursive", fontSize: "1.4rem" }}>Signature</span>
                            </SelectItem>
                            <SelectItem value="'Satisfy', cursive">
                              <span style={{ fontFamily: "'Satisfy', cursive", fontSize: "1.4rem" }}>Signature</span>
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex h-48 items-center justify-center overflow-hidden rounded-md border bg-gray-50">
                        {typedSignature ? (
                          <span className="text-5xl text-black" style={{ fontFamily: selectedFont }}>
                            {typedSignature}
                          </span>
                        ) : (
                          <span className="text-gray-400">Preview will appear here</span>
                        )}
                      </div>
                      <canvas ref={hiddenCanvasRef} width={600} height={200} className="hidden" />
                    </TabsContent>
                  </Tabs>

                  <div className="flex justify-between items-center mt-6 pt-6 border-t border-gray-100">
                    <Button variant="outline" onClick={clearSignature}>
                      Clear Signature
                    </Button>
                    <Button onClick={handleProviderSign} disabled={isLoading} className="bg-blue-600 hover:bg-blue-700">
                      {isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                      Sign & Continue
                    </Button>
                  </div>
                </div>
              )}

              {currentStep === "client_request" && (
                <div className="flex flex-col items-center justify-center text-center h-full space-y-6 pt-10">
                  <div className="h-20 w-20 rounded-full bg-blue-50 flex items-center justify-center">
                    <Mail className="h-10 w-10 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">Request Client Signature</h3>
                    <p className="text-gray-500 mt-2 max-w-md mx-auto">
                      Send an email request to your client containing a secure link to review and sign the agreement.
                    </p>
                  </div>
                  
                  <div className="w-full max-w-sm mt-6 text-left space-y-4">
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1 block">Client Name</label>
                      <Input
                        type="text"
                        placeholder="e.g. John Doe"
                        value={clientName}
                        onChange={(e) => setClientName(e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1 block">Client Email Address</label>
                      <Input
                        type="email"
                        placeholder="client@example.com"
                        value={clientEmail}
                        onChange={(e) => setClientEmail(e.target.value)}
                      />
                    </div>
                  </div>

                  <Button 
                    size="lg" 
                    className="w-full max-w-sm mt-4 bg-blue-600 hover:bg-blue-700"
                    onClick={handleSendToClient}
                    disabled={isLoading || !clientEmail}
                  >
                    {isLoading ? <><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Sending...</> : "Send Request Email"}
                  </Button>
                </div>
              )}

              {currentStep === "waiting" && (
                <div className="flex flex-col items-center justify-center text-center h-full space-y-6 pt-10">
                  <div className="h-20 w-20 rounded-full bg-amber-50 flex items-center justify-center">
                    <Clock className="h-10 w-10 text-amber-500" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">Waiting for Client</h3>
                    <p className="text-gray-500 mt-2 max-w-md mx-auto leading-relaxed">
                      The signature request has been sent successfully to <strong>{clientEmail}</strong>. We are now waiting for the client to review and sign the document. 
                      <br /><br />
                      You can safely leave this page. We will notify you via email when the client has completed the signing process.
                    </p>
                  </div>
                  <div className="flex gap-4 mt-6">
                    <Button 
                      variant="outline" 
                      onClick={() => navigate(-1)}
                    >
                      Return to Document
                    </Button>
                    <Button 
                      variant="secondary" 
                      onClick={handleSendToClient}
                      disabled={isLoading || !clientEmail}
                    >
                      {isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                      Resend Email Request
                    </Button>
                  </div>
                </div>
              )}

              {currentStep === "completed" && (
                <div className="flex flex-col items-center justify-center text-center h-full space-y-6 pt-10">
                  <div className="h-20 w-20 rounded-full bg-green-50 flex items-center justify-center">
                    <CheckCircle2 className="h-10 w-10 text-green-600" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">Agreement Completed</h3>
                    <p className="text-gray-500 mt-2 max-w-md mx-auto">
                      Both parties have signed the agreement. The final executed document has been emailed to the client and saved to your workspace.
                    </p>
                  </div>
                  <Button 
                    className="mt-6 bg-green-600 hover:bg-green-700"
                    onClick={() => navigate(-1)}
                  >
                    View Final Document
                  </Button>
                </div>
              )}
            </Card>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
